import { HorizontalJustification, WallHorizontalDatum } from "building-types/datums/datumsTypesEnum";
import { DatumHorizontalUsageType, datumUsageNew } from "building-types/datums/datumUsageTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import { FoundationAssemblyType } from "building-types/foundations/foundationAssemblyTypesEnum";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import { isStemWall, isCurb } from "building-types/foundations/foundationComponents";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { RoomConnection } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { ExteriorWallOffsetMode } from "building-types/settings/settingsTypesEnum";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { PathPriority } from "building-types/walls/pathPriority";
import type { WallAssemblyOperator } from "building-types/walls/wallAssemblyOverrideTypes";
import type { WallAssembly, WallAssemblyLayer } from "building-types/walls/wallAssemblyTypes";
import type { WallSection, WallSideDescriptor } from "building-types/walls/wallDesciptorTypes";
import { StructuralAssemblyType, WallCladdingType } from "building-types/walls/wallDesciptorTypes";
import type {
  HalfEdgeOffsets,
  WallEndButtJoin,
  WallEndCap,
  WallEndCapSource,
  WallEndMiterJoin,
} from "building-types/walls/wallEndsTypes";
import { ButtCapMode, EndCapSourceType, WallEndJoinType } from "building-types/walls/wallEndsTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { StructuralMaterialTable } from "building-types/walls/wallMaterialTypes";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { WallTable } from "building-types/walls/wallTableTypes";
import type { WallConditionTable, WallTypeTable } from "building-types/walls/wallType";
import { WallCondition } from "building-types/walls/wallType";
import type {
  SolidWallMass,
  VoidWall,
  Wall,
  WallLayer,
  WallMass,
  WallPath,
  WallPathBase,
} from "building-types/walls/wallTypes";
import { FREEFORM_WALL_HALF_EDGE_INDEX, WallLOD } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined, assertIsNumber } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { levelInchesNew } from "common/utils/nominalNumbers";
import type { Mutable } from "common/utils/types";
import uid from "common/utils/uid";
import { isInteriorRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { getOpenConnectedRoomSets } from "doc/rooms/roomTraversal";
import {
  isExteriorWallCondition,
  getExteriorWallStructualOffset,
  REFERENCE_EXTERIOR_WALL_CONDITION,
  isFoundationWallCondition,
  getWallTypeByWallCondition,
} from "doc/utils/wallTypeTools";
import { closestParamNormalizedLine2 } from "geom/closestPoint";
import { ANGLE_EPSILON, DIST_EPSILON } from "geom/core";
import { line2Eval, line2Normal, line2Tangent } from "geom/line2";
import type { PlanarGraph, PlanarGraphFacePoleOfInaccessibilityPositions } from "geom/planarGraph";
import {
  planarGraphAdjacentHalfEdge,
  planarGraphHalfEdgeStart,
  planarGraphMakeFacePoleOfInaccessibilityPositions,
  planarGraphNextHalfEdgeOnCollinearStrip,
  planarGraphPrevHalfEdgeOnCollinearStrip,
} from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2Reverse } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2Dist, vec2Dot } from "geom/vec2";
import { isCombinedGlobalInstanceMirror } from "../../generationTools/generateOpeningsTools";
import type { WallDatumAbstractionProvider } from "../datums/datumAbstractionProviders";
import { wallAssemblyHorizontalDatumNew } from "../datums/datumEntity";
import { curbWidth } from "../foundation/curb";
import { stemWallWidth } from "../foundation/stemWall";
import {
  getWallAssemblyFinishThickness,
  getWallAssemblyFinishThicknessAtWallPathParam,
  getWallAssemblyStructuralThickness,
  getWallAssemblySubstrateThickness,
  makeWallAssemblyFromWallType,
  wallAssemblyNew,
} from "../wallAssemblies/wallAssembly";
import { defaultWallAssemblyLayerDomain } from "../wallAssemblies/wallAssemblyDefaults";
import {
  applyWallAssemblyOperatorsToWallAssembly,
  isWallAssemblyLayerOperator,
  isWallAssemblyLayerRemoveOperator,
} from "../wallAssemblies/wallAssemblyOverride";
import { collectFreeformWallOpeningCuts } from "../walls2/freeformWallOpeningCuts";
import { solidWallMassFromWallEnds, solidWallWithOpenings } from "./solidWall";
import { voidWallFromHalfEdgeOffsets } from "./voidWall";
import { getWallPath, getWallSideRooms } from "./wall";
import { wallLayerNew } from "./wall";
import type { HalfEdgeToDoorSet, HalfEdgeToWallCutSet, HalfEdgeToWindowSet } from "./wallCut";
import { wallPathOpposite } from "./wallDescriptor";
import { isVoidWallSection, makeWallBaseSection, makeWallCoreSections, makeWallFinishSections } from "./wallDescriptor";
import {
  halfEdgeOffsetsExtend,
  halfEdgeOffsetsNew,
  halfEdgeOffsetsOffset,
  halfEdgeOffsetsOpposite,
  makeFaceWallEndCaps,
  makeVertexHalfEdgeWallEndCapSources,
  makeWallEndCaps,
  makeWallEndCapSources,
} from "./wallEnds";
import { WallIndexImpl } from "./wallIndexImpl";
import { wallLayerWithFallback, wallMassLayers } from "./wallLayers";
import { makeWallSideFinishMasses, wallSidePathDomainReverse } from "./wallSide";
import { wallSideNew } from "./wallSideBasics";

interface MakeWallMassSystemOptions {
  wallDatumAbstractionProvider: WallDatumAbstractionProvider;
  rooms: Room[];
  wallSettings: WallSettings;
  wallCatalogSettings: WallCatalogSettings;
  foundationComponents: Table<FoundationComponentEntity>;
  planarGraph: PolygonPlanarGraph;
  roomConnections: RoomConnection[][];
  roomLevelIndices: Table<number>;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
  /**
   * @TODO - remove this parameter and replace with
   * some kind of planar graph floor elevation context
   */
  levelId: string;
  levels: Table<Level>;
  insideCornerHalfEdgeLookup: { [halfEdge: number]: boolean };
  mirror: boolean;
  wallPaths: WallPath[];
}

function adjustWallConditionAtWallSection(wallCondition: WallCondition, wallSection: WallSection): WallCondition {
  const { leftSide, rightSide } = wallSection;
  const wallSectionExterior =
    leftSide.cladding === WallCladdingType.EXTERIOR || rightSide.cladding === WallCladdingType.EXTERIOR;
  if (wallCondition === WallCondition.INTERIOR_INTERIOR && wallSectionExterior) {
    return WallCondition.EXTERIOR_INTERIOR;
  }
  if (wallCondition === WallCondition.INTERIOR_GARAGE && wallSectionExterior) {
    return WallCondition.EXTERIOR_GARAGE;
  }
  if (wallCondition === WallCondition.INTERIOR_PLUMBING && wallSectionExterior) {
    return WallCondition.EXTERIOR_PLUMBING;
  }
  // TODO- this may need to be expanded
  return wallCondition;
}

/**
 * Wall offsets on the planar graph are generated such that the assembly structure is centered on the
 * planar graph edge by default. This does not match up with the desired positioning of walls for exterior
 * alignment, or for resolving structural overrides on either side of an edge.
 *
 * In order to compensate for this and align walls to their desired locations, this function
 * calculates the offset needed to be applied to the wall to align with the exterior edges, as well
 * as any user defined overrides to wall positioning.
 *
 * @param defaultWallAssembly is used only to maintain an existing bug in offsets without disrupting baselines
 */
function makeWallAlignmentOffset(
  wallPath: WallPath,
  wallSection: WallSection,
  wallAssembly: WallAssembly,
  defaultWallAssembly: WallAssembly,
  wallPaths: WallPath[],
  opts: MakeWallMassSystemOptions,
) {
  const { planarGraph, insideCornerHalfEdgeLookup, wallSettings, wallCatalogSettings } = opts;

  const { wallMaterials } = wallCatalogSettings;

  const {
    interiorWallOffset,
    exteriorWallOffset,
    exteriorWallOffsetMode,
    exteriorWallOffsetDatum,
    insideCornerWallOffsetForInteriorWallThickening,
    insideCornerWallOffsetForInteriorRoofWallThickening,
    outsideCornerWallOffsetForInteriorWallThickening,
  } = wallSettings;

  const { exteriorWallOffset: exteriorWallOffsetAdjusted, exteriorWallOffsetMode: exteriorWallOffsetModeAdjusted } =
    adjustWallOffsetsIfInsideCornerAndInteriorWallThickeningMode(
      exteriorWallOffset,
      exteriorWallOffsetMode,
      insideCornerHalfEdgeLookup,
      wallPath,
      insideCornerWallOffsetForInteriorWallThickening,
      insideCornerWallOffsetForInteriorRoofWallThickening,
    );

  // true thickness for this assembly
  const structuralThickness = getWallAssemblyStructuralThickness(wallMaterials, wallAssembly);
  if (
    wallSection.leftSide.structure === StructuralAssemblyType.NONE &&
    wallSection.rightSide.structure === StructuralAssemblyType.NONE &&
    structuralThickness === 0
  ) {
    // No structure from anywhere (no wall, no foundation, no floor, no roof, etc...).
    // Offset is not used
    return 0;
  }

  /**
   * Default thicknesses for exterior offsets - we use this for the time being to maintain baselines.
   * @NOTE - using the defaultStructuralThickness is a bug, but fixing it will break baselines
   * */
  const defaultStructuralThickness = getWallAssemblyStructuralThickness(wallMaterials, defaultWallAssembly);
  const defaultInteriorWallThickness = interiorWallOffset * 2;

  const getWallSideExteriorOffset = (isLeftSide: boolean, calculateSpecificOffset: boolean) => {
    let exteriorOffset = exteriorWallOffsetAdjusted;
    if (calculateSpecificOffset) {
      const substrateThickness = getWallAssemblySubstrateThickness(
        wallMaterials,
        wallAssembly,
        wallSection,
        isLeftSide,
      );
      exteriorOffset = getExteriorWallStructualOffset(
        defaultStructuralThickness,
        substrateThickness,
        defaultInteriorWallThickness,
        exteriorWallOffsetModeAdjusted,
        exteriorWallOffsetDatum,
        outsideCornerWallOffsetForInteriorWallThickening,
      );
    }
    return exteriorOffset;
  };

  const leftSideExterior = wallSideUsesExteriorWallOffset(wallPath, true);
  const rightSideExterior = wallSideUsesExteriorWallOffset(wallPath, false);

  /**
   * Use to align the wall left or right edges to the planarGraph
   * leftEdgeAlignment will align the wall such that the left side of the wall is aligned with the planarGraph
   * rightEdgeAlignment will align the wall such that the right side of the wall is alignent with the planarGraph
   */
  const leftEdgeAlignment = structuralThickness / 2;
  const rightEdgeAlignment = -structuralThickness / 2;

  const { structuralPriority, structuralOffset } = wallAssembly;

  let wallSideOffset = 0;

  if (leftSideExterior || rightSideExterior) {
    /**
     * Exterior/Exterior and Exterior/Interior offsets are calculated by using the alignment defined by the
     * user wall settings, in order to maintain a consistent alignment around the
     * exterior of the house.
     *
     * This is achieved by calculating the exterior offset
     * independently of the structural thickness, and offsetting by that amount.
     */

    const calculateSpecificOffset = wallHasSpecificExteriorStructuralOffset(
      planarGraph.planarGraph,
      wallPaths,
      wallPath,
    );

    const leftExteriorOffset = getWallSideExteriorOffset(true, calculateSpecificOffset);
    const rightExteriorOffset = getWallSideExteriorOffset(false, calculateSpecificOffset);

    if (leftSideExterior && rightSideExterior) {
      wallSideOffset = 0;
    } else if (leftSideExterior) {
      wallSideOffset = leftEdgeAlignment - leftExteriorOffset;
    } else {
      wallSideOffset = rightEdgeAlignment + rightExteriorOffset;
    }
  } else {
    /**
     * Interior/Interior offsets are calculated by looking at wall overrides
     * on either side of the half edge.
     *
     * In the even that the structural thickness is different on either side of a half edge
     * the wall offset is aligned such that the largest override wins and the wall is offset by
     * half the default wall thicknesson the smaller side, and the remainder of the FULL structural thickness
     * on the larger side
     */

    const leftInteriorOffset = structuralThickness - interiorWallOffset;
    const rightInteriorOffset = structuralThickness - interiorWallOffset;

    if (structuralPriority === PathPriority.NONE) {
      wallSideOffset = 0;
    } else if (structuralPriority === PathPriority.LEFT) {
      wallSideOffset = leftEdgeAlignment - leftInteriorOffset;
    } else {
      wallSideOffset = rightEdgeAlignment + rightInteriorOffset;
    }
  }

  return wallSideOffset + structuralOffset;
}

/**
 * Wall Sections that do not have a StructuralType of @type {StructuralAssemblyType.WALL} on the outside may not have the desired offsets
 * calculated by the @function { makeWallAlignmentOffset } above. Their wall assemblies are generated based on the Structural Type and Cladding Type,
 * but should still be aligned to the assembly of the base wall if possible.
 */
function makeWallSectionAlignmentOffset(
  wallPath: WallPath,
  wallAssemblySections: WallAssemblySection[],
  wallAssemblySectionIndex: number,
  wallSettings: WallSettings,
): number {
  const wallAssemblySection = wallAssemblySections[wallAssemblySectionIndex];
  const { wallAssemblyOffsets, leftSide, rightSide } = wallAssemblySection;
  const { wallDescriptor } = wallPath;
  const { floorPriority } = wallDescriptor;
  if (floorPriority === PathPriority.NONE) {
    return 0;
  }

  // we use the OUTSIDE structural type for alignment, so PathPriority.LEFT => RIGHT side and PathPriority.RIGHT => LEFT side
  const wallSectionSide = floorPriority === PathPriority.LEFT ? rightSide : leftSide;

  /**
   * Calculate the default offset for this section based on wall settings
   * and the cladding type at this section.
   */
  const getWallSettingsLayerOffset = (wallLayer: WallLayerType) => {
    switch (wallSectionSide.cladding) {
      case WallCladdingType.INTERIOR: {
        return wallSettings.interiorWallOffset;
      }
      case WallCladdingType.EXTERIOR:
      case WallCladdingType.NONE: {
        let wallSettingsOffset = wallSettings.exteriorWallOffset;
        if (wallLayer !== WallLayerType.STRUCTURE) {
          wallSettingsOffset += wallSettings.exteriorWallSubstrateThickness;
        }
        return wallSettingsOffset;
      }
      default: {
        assertUnreachable(wallSectionSide.cladding);
      }
    }
  };

  /**
   * Trace wall sections until we hit a wall with a structural type of WALL,
   * and return the offset on the side that's aligned to the outside of this edge.
   * If no wall section of type WALL exists, default back to the wall settings.
   */
  const getWallSectionLayerOffset = (
    wallLayer: WallLayerType,
    iterableWallSections: Generator<WallAssemblySection>,
  ) => {
    let wallSectionOffsetAbove: number | undefined;

    for (const wallSection of iterableWallSections) {
      const { wallAssemblyOffsets } = wallSection;
      const offsetSideStructure =
        floorPriority === PathPriority.LEFT ? wallSection.rightSide.structure : wallSection.leftSide.structure;
      if (offsetSideStructure !== StructuralAssemblyType.WALL) {
        continue;
      }
      const { leftOffset, rightOffset } = wallAssemblyOffsets[wallLayer];
      wallSectionOffsetAbove = floorPriority === PathPriority.LEFT ? rightOffset : leftOffset;
      break;
    }

    return wallSectionOffsetAbove ?? getWallSettingsLayerOffset(wallLayer);
  };

  let wallSectionIterator: Generator<WallAssemblySection>;
  let wallSectionReferenceLayer: WallLayerType | WallLayerTypeAdditional; // offsets from this section to use
  let wallSectionAlignmentLayer: WallLayerType; // offsets from the wall to align to
  let wallSectionAdditionalOffset = 0; // additional optional offset
  switch (wallSectionSide.structure) {
    case StructuralAssemblyType.FOUNDATION: {
      const { foundationAssembly } = wallDescriptor;
      assertIsNotUndefined(foundationAssembly);
      const { lateralDatumAbstraction } = foundationAssembly;
      /**
       * Foundation Sections align their FOUNDATION offsets to the anchor layer of the foundation assembly
       * of the nearest wall section above. If this is a slab, the foundation offset is located in the STRUCTURE offsets
       */
      wallSectionReferenceLayer = wallAssemblyOffsets[WallLayerTypeAdditional.FOUNDATION]
        ? WallLayerTypeAdditional.FOUNDATION
        : WallLayerType.STRUCTURE;
      wallSectionAlignmentLayer = lateralDatumAbstraction.resolveWallLayer() ?? WallLayerType.STRUCTURE;
      wallSectionIterator = nextLayerIterator(wallAssemblySections, wallAssemblySectionIndex);
      wallSectionAdditionalOffset = lateralDatumAbstraction.resolveLateralDatum();
      break;
    }
    case StructuralAssemblyType.FLOOR:
      /**
       * Floor Sections align their STRUCTURE layer to the STRUCTURE layer of the wall assembly
       * of the nearest wall section above
       */
      wallSectionReferenceLayer = WallLayerType.STRUCTURE;
      wallSectionAlignmentLayer = WallLayerType.STRUCTURE;
      wallSectionIterator = nextLayerIterator(wallAssemblySections, wallAssemblySectionIndex);
      break;
    case StructuralAssemblyType.CEILING:
    case StructuralAssemblyType.ROOF: {
      /**
       * Roof Sections align their STRUCTURE layer to the STRUCTURE layer of the wall assembly
       * of the nearest wall section below
       */
      wallSectionReferenceLayer = WallLayerType.STRUCTURE;
      wallSectionAlignmentLayer = WallLayerType.STRUCTURE;
      wallSectionIterator = prevLayerIterator(wallAssemblySections, wallAssemblySectionIndex);
      break;
    }
    case StructuralAssemblyType.NONE:
    case StructuralAssemblyType.WALL: {
      return 0;
    }
    default: {
      assertUnreachable(wallSectionSide.structure);
    }
  }

  const wallSectionReferenceOffsets = wallAssemblyOffsets[wallSectionReferenceLayer];
  let wallSideReferenceOffset: number;
  let wallSideMult = 1;

  assertIsNotUndefined(wallSectionReferenceOffsets);

  if (floorPriority === PathPriority.LEFT) {
    wallSideReferenceOffset = wallSectionReferenceOffsets.rightOffset;
    wallSideMult = -1; // flip the offset
  } else {
    wallSideReferenceOffset = wallSectionReferenceOffsets.leftOffset;
  }

  const wallSectionAlignmentOffset = getWallSectionLayerOffset(wallSectionAlignmentLayer, wallSectionIterator);
  return wallSideMult * (wallSideReferenceOffset - wallSectionAlignmentOffset - wallSectionAdditionalOffset);
}

/**
 * Botom-up iterator of sections below this layer index
 */
function* nextLayerIterator(sections: WallAssemblySection[], index: number) {
  for (let i = index + 1; i < sections.length; i++) {
    yield sections[i];
  }
}

/**
 * Top-down iterator of sections above this layer index
 */
function* prevLayerIterator(sections: WallAssemblySection[], index: number) {
  for (let i = index - 1; i >= 0; i--) {
    yield sections[i];
  }
}

function adjustWallOffsetsIfInsideCornerAndInteriorWallThickeningMode(
  exteriorWallOffset: number,
  exteriorWallOffsetMode: ExteriorWallOffsetMode,
  insideCornerHalfEdgeLookup: { [halfEdge: number]: boolean },
  wallPath: WallPath,
  insideCornerWallOffsetForInteriorWallThickening: number,
  insideCornerWallOffsetForInteriorRoofWallThickening: number,
) {
  let exteriorWallOffsetCopy = exteriorWallOffset;
  let exteriorWallOffsetModeCopy = exteriorWallOffsetMode;
  if (
    exteriorWallOffsetMode === ExteriorWallOffsetMode.INSIDE_THICKENING ||
    exteriorWallOffsetMode === ExteriorWallOffsetMode.INSIDE_THICKENING_ALL
  ) {
    if (insideCornerHalfEdgeLookup[wallPath.halfEdgeIndex]) {
      if (wallPath.wallCondition === WallCondition.INTERIOR_ROOF) {
        exteriorWallOffsetCopy = insideCornerWallOffsetForInteriorRoofWallThickening;
      } else {
        if (exteriorWallOffsetMode === ExteriorWallOffsetMode.INSIDE_THICKENING) {
          exteriorWallOffsetCopy = insideCornerWallOffsetForInteriorWallThickening;
        }
      }

      exteriorWallOffsetModeCopy = ExteriorWallOffsetMode.OUTSIDE_THICKENING;
    }
  }
  return { exteriorWallOffset: exteriorWallOffsetCopy, exteriorWallOffsetMode: exteriorWallOffsetModeCopy };
}

function isSideValidForSpecificExteriorWallOffset(
  planarGraph: PlanarGraph,
  wallPaths: WallPath[],
  wallPath: WallPath,
  getCollinearStrip: (planarGraph: PlanarGraph, halfEdgeIndex: number) => number | null,
): boolean {
  if (!wallPath) {
    return true;
  }

  const { wallCondition } = wallPath;

  if (!isExteriorWallCondition[wallCondition]) {
    return false;
  }

  if (wallCondition === REFERENCE_EXTERIOR_WALL_CONDITION) {
    return false;
  }

  if (isFoundationWallCondition[wallCondition]) {
    return false;
  }

  const idx = getCollinearStrip(planarGraph, wallPath.halfEdgeIndex);
  const nextCollinearPath = idx !== null ? wallPaths[idx] : null;

  if (!nextCollinearPath || !isExteriorWallCondition[nextCollinearPath.wallCondition]) {
    return true;
  }

  if (nextCollinearPath.wallCondition !== wallCondition && nextCollinearPath.wallType !== WallType.VOID) {
    return false;
  }

  return isSideValidForSpecificExteriorWallOffset(planarGraph, wallPaths, nextCollinearPath, getCollinearStrip);
}

function wallHasSpecificExteriorStructuralOffset(
  planarGraph: PlanarGraph,
  wallPaths: WallPath[],
  wallPath: WallPath,
): boolean {
  const getNext = planarGraphNextHalfEdgeOnCollinearStrip;
  const getPrev = planarGraphPrevHalfEdgeOnCollinearStrip;
  return (
    isSideValidForSpecificExteriorWallOffset(planarGraph, wallPaths, wallPath, getNext) &&
    isSideValidForSpecificExteriorWallOffset(planarGraph, wallPaths, wallPath, getPrev)
  );
}

/**
 * Some exterior walls have specific offsets defined by the relationship between the rooms
 * on either side of the half edge.
 * @param wallPath
 * @param isLeft
 * @returns
 */
function wallSideUsesExteriorWallOffset(wallPath: WallPath, isLeft: boolean) {
  const { leftRoom, rightRoom } = wallPath;
  const leftEnc = leftRoom?.attributes.roomEnclosure;
  const leftRoomInterior = leftEnc ? isInteriorRoomEnclosure[leftEnc] : undefined;
  const leftRoomWalled = leftEnc ? isWalledRoomEnclosure[leftEnc] : undefined;

  const rightEnc = rightRoom?.attributes.roomEnclosure;
  const rightRoomInterior = rightEnc ? isInteriorRoomEnclosure[rightEnc] : undefined;
  const rightRoomWalled = rightEnc ? isWalledRoomEnclosure[rightEnc] : undefined;

  if (isLeft) {
    if (!leftRoom || !leftRoomWalled) {
      return true;
    }
    if (!leftRoomInterior && rightRoomInterior) {
      return true;
    }
  }

  if (!isLeft) {
    if (!rightRoom || !rightRoomWalled) {
      return true;
    }
    if (!rightRoomInterior && leftRoomInterior) {
      return true;
    }
  }

  return false;
}

export function makeDefaultWallAssembly(
  wallMaterials: StructuralMaterialTable,
  wallPath: WallPath,
  wallSection: WallSection,
  wallConditions: WallConditionTable,
  wallTypes: WallTypeTable,
) {
  const { wallDescriptor } = wallPath;
  const { foundationAssembly } = wallDescriptor;

  assertIsNotUndefined(foundationAssembly);

  const wallCondition = adjustWallConditionAtWallSection(wallPath.wallCondition, wallSection);
  let wallType = getWallTypeByWallCondition(wallConditions, wallTypes, wallCondition);
  if (foundationAssembly.assemblyType !== FoundationAssemblyType.VOID) {
    const { stemWall, curb } = foundationAssembly;
    if (stemWall) {
      wallType = {
        ...wallType,
        foundationMaterialId: stemWall.componentId,
      };
    } else if (curb) {
      wallType = {
        ...wallType,
        foundationMaterialId: curb.componentId,
      };
    }
  }

  return makeWallAssemblyFromWallType({
    wallMaterials,
    wallSection,
    wallDescriptor,
    wallType,
  });
}

enum WallLayerTypeAdditional {
  FOUNDATION = "FOUNDATION",
  CORE = "CORE",
}

interface WallAssemblyOffsets {
  [WallLayerType.STRUCTURE]: HalfEdgeOffsets;
  [WallLayerType.SUBSTRATE]: HalfEdgeOffsets;
  [WallLayerType.FINISH]: HalfEdgeOffsets;
  [WallLayerTypeAdditional.FOUNDATION]?: HalfEdgeOffsets;
  [WallLayerTypeAdditional.CORE]?: HalfEdgeOffsets;
}

interface WallAssemblySection extends WallSection {
  readonly wallAssembly: WallAssembly;
  readonly wallAssemblyOffsets: WallAssemblyOffsets;
}

function getWallAssemblyFoundationThickness(
  wallPath: WallPath,
  wallAssembly: WallAssembly,
  opts: MakeWallMassSystemOptions,
) {
  const { wallDescriptor } = wallPath;
  const { foundationCondition } = wallDescriptor;
  const { foundationComponents, wallSettings } = opts;
  const { interiorWallOffset } = wallSettings;

  let foundationWallThickness = 0;
  const { foundationLayers } = wallAssembly;
  for (const layer of foundationLayers) {
    const foundationComponent = foundationComponents[layer.foundationComponentId];
    if (isStemWall(foundationComponent)) {
      foundationWallThickness += stemWallWidth({
        path: { condition: foundationCondition },
        stemWall: foundationComponent,
      }).width;
    } else if (isCurb(foundationComponent)) {
      /**
       * @TODO - The offsets used here will not match up with the true value of the curb,
       * unless it is a user specified curb. This is because the curb will have a thickness
       * value equal to the wall structural thickness from the wall assembly ABOVE the current assembly.
       * The default values will hide rendering artifacts for the time being.
       */
      foundationWallThickness += curbWidth({
        floorPath: {
          leftWallOffset: interiorWallOffset,
          rightWallOffset: interiorWallOffset,
        },
        curb: foundationComponent,
      }).width;
    }
  }
  return foundationWallThickness;
}

function makeWallAssemblyCoreSection(
  level: Level,
  wallPath: WallPath,
  wallSection: WallSection,
  wallPaths: WallPath[],
  leftOperators: WallAssemblyOperator[],
  rightOperators: WallAssemblyOperator[],
  opts: MakeWallMassSystemOptions, // @TODO -consider only passing needed params so we can test this
): WallAssemblySection {
  const { wallCatalogSettings, wallSettings, wallDatumAbstractionProvider } = opts;
  const { wallMaterials, wallTypes } = wallCatalogSettings;
  const { wallConditions } = wallSettings;

  const { wallDescriptor, wallType: wallPathType } = wallPath;
  const { foundationAssembly } = wallDescriptor;

  let defaultWallAssembly = makeDefaultWallAssembly(wallMaterials, wallPath, wallSection, wallConditions, wallTypes);

  let wallAssembly = applyWallAssemblyOperatorsToWallAssembly({
    level,
    wallDatumAbstractionProvider,
    wallPath,
    wallDescriptor,
    wallSection,
    wallAssembly: defaultWallAssembly,
    leftOperators,
    rightOperators,
    wallMaterials,
  });

  assertIsNotUndefined(foundationAssembly);

  /**
   * If this is a furring wall and the wall has been voided, the foundation still
   * exists on the wall, but all other layers need to be removed.
   * However, facades can still be applied to this wall
   */
  if (
    foundationAssembly.assemblyType !== FoundationAssemblyType.VOID &&
    wallDescriptor.isFurring &&
    wallPathType === WallType.VOID
  ) {
    defaultWallAssembly = {
      ...defaultWallAssembly,
      structuralLayers: [],
      leftCladdingLayers: [],
      rightCladdingLayers: [],
      extraLeftLayers: [],
      extraRightLayers: [],
    };
    wallAssembly = {
      ...wallAssembly,
      structuralLayers: [],
      leftCladdingLayers: [],
      rightCladdingLayers: [],
      extraLeftLayers: [],
      extraRightLayers: [],
    };
  }

  const wallAlignmentOffset = makeWallAlignmentOffset(
    wallPath,
    wallSection,
    wallAssembly,
    defaultWallAssembly,
    wallPaths,
    opts,
  );

  const leftFinishThickness = getWallAssemblyFinishThickness(wallMaterials, wallAssembly, wallSection, true);
  const leftSubstrateThickness = getWallAssemblySubstrateThickness(wallMaterials, wallAssembly, wallSection, true);
  const structuralThickness = getWallAssemblyStructuralThickness(wallMaterials, wallAssembly);
  const foundationThickness = getWallAssemblyFoundationThickness(wallPath, wallAssembly, opts);
  const rightSubstrateThickness = getWallAssemblySubstrateThickness(wallMaterials, wallAssembly, wallSection, false);
  const rightFinishThickness = getWallAssemblyFinishThickness(wallMaterials, wallAssembly, wallSection, false);

  const wallBaseOffsets = halfEdgeOffsetsNew(structuralThickness / 2, structuralThickness / 2);

  let wallStructure = halfEdgeOffsetsOffset(wallBaseOffsets, wallAlignmentOffset);
  let wallSubstrate = halfEdgeOffsetsExtend(wallStructure, leftSubstrateThickness, rightSubstrateThickness);
  let wallFinish = halfEdgeOffsetsExtend(wallSubstrate, leftFinishThickness, rightFinishThickness);

  const { floorPriority } = wallPath.wallDescriptor;
  const wallAssemblyOffsetsAdditional: Partial<WallAssemblyOffsets> = {};
  if (foundationThickness && floorPriority !== PathPriority.NONE) {
    /**
     * This is a wall with foundation thickness
     * - the interior and exterior STRUCTURE offsets need to move to the inside of foundationWallThicknes.
     * - the interior SUBSTRATE offset needs to move to the interior of the new STUCTURAL offset
     * - the exterior SUBSTRATE offset needs to align to the exterior FOUNDATION offset
     *
     * @TODO - consider exposing CORE and optional FOUNDATION layers on the wall and removing MASS. These should not have faces or masses
     * generated for them, but will be helpful in creating the appropriate alignments and finish cross sections
     */

    let wallFoundation: HalfEdgeOffsets | undefined;
    let wallCore: HalfEdgeOffsets | undefined;
    // apply foundation wall thickness
    if (floorPriority === PathPriority.LEFT) {
      wallStructure = halfEdgeOffsetsOffset(wallStructure, -foundationThickness);
      wallFoundation = halfEdgeOffsetsExtend(wallStructure, -structuralThickness, foundationThickness);
      wallCore = halfEdgeOffsetsNew(wallStructure.leftOffset, wallFoundation.rightOffset);
    } else if (floorPriority === PathPriority.RIGHT) {
      wallStructure = halfEdgeOffsetsOffset(wallStructure, foundationThickness);
      wallFoundation = halfEdgeOffsetsExtend(wallStructure, foundationThickness, -structuralThickness);
      wallCore = halfEdgeOffsetsNew(wallFoundation.leftOffset, wallStructure.rightOffset);
    }
    const useNewWallLayerSystem = true; // TODO - check implications of removing this

    assertIsNotUndefined(wallCore);

    if (useNewWallLayerSystem) {
      wallSubstrate = halfEdgeOffsetsExtend(wallCore, leftSubstrateThickness, rightSubstrateThickness);
      wallFinish = halfEdgeOffsetsExtend(wallSubstrate, leftFinishThickness, rightFinishThickness);
    } else {
      wallSubstrate = halfEdgeOffsetsExtend(wallStructure, leftSubstrateThickness, rightSubstrateThickness);
      wallFinish = halfEdgeOffsetsExtend(wallCore, leftFinishThickness, rightFinishThickness);
    }

    wallAssemblyOffsetsAdditional[WallLayerTypeAdditional.CORE] = wallCore;
    wallAssemblyOffsetsAdditional[WallLayerTypeAdditional.FOUNDATION] = wallFoundation;
  }

  const wallAssemblyOffsets: WallAssemblyOffsets = {
    [WallLayerType.STRUCTURE]: wallStructure,
    [WallLayerType.SUBSTRATE]: wallSubstrate,
    [WallLayerType.FINISH]: wallFinish,
    ...wallAssemblyOffsetsAdditional,
  };

  return Object.assign({}, wallSection, {
    wallAssembly,
    wallAssemblyOffsets,
  });
}

/**
 * @NOTE - @param wallSubstrateMass may not be aligned to the wallPath.
 * @TODO - needs refactoring for a lot of clarity here. We should store all the wallMasses
 * until generation is complete
 */
function makeWallAssemblyFinishSection(
  level: Level,
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallPath: WallPath,
  wallFinishSection: WallSection,
  wallSubstrateMass: WallMass,
  wallCoreSections: WallAssemblySection[],
  leftOperators: WallAssemblyOperator[],
  rightOperators: WallAssemblyOperator[],
  wallMaterials: StructuralMaterialTable,
): WallAssemblySection {
  const { wallDescriptor } = wallPath;
  const { bottomOffset, topOffset } = wallFinishSection;

  const wallCoreSection = wallCoreSections.find(
    (coreSection) => coreSection.bottomOffset <= bottomOffset && coreSection.topOffset >= topOffset,
  );

  let { leftDomain, rightDomain } = wallSubstrateMass;
  if (wallSubstrateMass.path.halfEdgeIndex !== wallPath.halfEdgeIndex) {
    leftDomain = wallSubstrateMass.rightDomain;
    rightDomain = wallSubstrateMass.leftDomain;
  }

  const leftDomainReversed = wallSidePathDomainReverse(leftDomain);

  const minStartOffset = Math.min(rightDomain.startT, leftDomainReversed.startT);
  const maxEndOffset = Math.max(rightDomain.endT, leftDomainReversed.endT);

  const defaultWallAssembly = wallCoreSection?.wallAssembly ?? wallAssemblyNew();

  const wallAssembly = applyWallAssemblyOperatorsToWallAssembly({
    level,
    wallDatumAbstractionProvider,
    wallPath,
    wallDescriptor,
    /**
     * @TODO - this is a temporary fix. Operators should filter layers by each domain side individually.
     * This catches a small edge case for now, but needs to be updated.
     */
    wallSection: { ...wallFinishSection, startOffset: minStartOffset, endOffset: maxEndOffset },
    wallAssembly: defaultWallAssembly,
    leftOperators,
    rightOperators,
    wallMaterials,
  });

  /**
   * Finish Miters are computed only using the finish thickness at the start of a wallPath.
   * This produces different miters/offsets for adjacent planar graph wallPaths, since they contain
   * only the thickness at that "end" of the wall.
   */
  const leftFinishThickness = getWallAssemblyFinishThicknessAtWallPathParam(
    wallDatumAbstractionProvider,
    wallMaterials,
    wallAssembly,
    wallPath,
    leftDomain.endT - DIST_EPSILON,
    wallFinishSection,
    true,
  );
  const rightFinishThickness = getWallAssemblyFinishThicknessAtWallPathParam(
    wallDatumAbstractionProvider,
    wallMaterials,
    wallAssembly,
    wallPath,
    rightDomain.startT + DIST_EPSILON,
    wallFinishSection,
    false,
  );

  const wallStructure = wallCoreSection?.wallAssemblyOffsets[WallLayerType.STRUCTURE] ?? halfEdgeOffsetsNew(0, 0);
  const wallSubstrate = wallCoreSection?.wallAssemblyOffsets[WallLayerType.SUBSTRATE] ?? halfEdgeOffsetsNew(0, 0);
  const wallFinish = halfEdgeOffsetsExtend(wallSubstrate, leftFinishThickness, rightFinishThickness);

  const wallAssemblyOffsets: WallAssemblyOffsets = {
    [WallLayerType.STRUCTURE]: wallStructure,
    [WallLayerType.SUBSTRATE]: wallSubstrate,
    ...wallCoreSection?.wallAssemblyOffsets,
    [WallLayerType.FINISH]: wallFinish,
  };

  return Object.assign({}, wallFinishSection, {
    wallAssembly,
    wallAssemblyOffsets,
  });
}

interface WallMassSystemSections {
  wallBaseSections: WallAssemblySection[];
  wallDatumSections: WallAssemblySection[][];
}

function makeWallAssemblyCoreSections(
  level: Level,
  wallPaths: WallPath[],
  opts: MakeWallMassSystemOptions,
): WallMassSystemSections {
  const wallBaseSections = new Array<WallAssemblySection>(wallPaths.length);
  const wallDatumSections = new Array<WallAssemblySection[]>(wallPaths.length);

  for (let halfEdge = 0; halfEdge < wallPaths.length; halfEdge++) {
    const wallPath = wallPaths[halfEdge];
    const { wallDescriptor } = wallPath;

    const wallPathBaseSection = makeWallBaseSection(wallDescriptor);
    const wallPathCoreSections = makeWallCoreSections(wallDescriptor);

    const leftOperators = wallDescriptor.wallOperators.leftCoreOperators;
    const rightOperators = wallDescriptor.wallOperators.rightCoreOperators;

    wallBaseSections[halfEdge] = makeWallAssemblyCoreSection(
      level,
      wallPath,
      wallPathBaseSection,
      wallPaths,
      leftOperators,
      rightOperators,
      opts,
    );
    wallDatumSections[halfEdge] = wallPathCoreSections.map((s) =>
      makeWallAssemblyCoreSection(level, wallPath, s, wallPaths, leftOperators, rightOperators, opts),
    );

    adjustWallSectionAlignment(wallPath, [wallBaseSections[halfEdge]], opts.wallSettings);
    adjustWallSectionAlignment(wallPath, wallDatumSections[halfEdge], opts.wallSettings);
  }

  return {
    wallBaseSections,
    wallDatumSections,
  };
}

/**
 * Remap WallFacade Spans to WallAssembly Finish Layers on the Wall domain
 * rather than the WallStrip domain
 *
 * @param wallCore wallMassSystem with SUBSTRATE and STRUCTURE layers
 */
function remapWallAssemblyOperators(
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallCore: WallMassSystem,
  mirror: boolean,
) {
  const { walls, wallIndex } = wallCore;
  const result: WallAssemblyOperator[][] = new Array(wallCore.halfEdgeToWall.length);

  for (const wallId in walls) {
    const wall = walls[wallId];
    const { path } = wall;

    const leftHalfEdge = path.halfEdgeIndex;
    const rightHalfEdge = planarGraphAdjacentHalfEdge(path.halfEdgeIndex);

    result[leftHalfEdge] = remapWallLayerDomain(
      wallDatumAbstractionProvider,
      wall,
      wallIndex,
      path.wallDescriptor.wallOperators.leftFinishOperators,
      true,
      mirror,
    );
    result[rightHalfEdge] = remapWallLayerDomain(
      wallDatumAbstractionProvider,
      wall,
      wallIndex,
      path.wallDescriptor.wallOperators.rightFinishOperators,
      false,
      mirror,
    );
  }

  return result;
}

function makeWallAssemblyFinishSections(
  level: Level,
  wallPaths: WallPath[],
  wallCore: WallMassSystem,
  wallCoreSections: WallMassSystemSections,
  opts: MakeWallMassSystemOptions,
): WallMassSystemSections {
  const { walls: wallCoreWalls } = wallCore;
  const { wallCatalogSettings, mirror, wallDatumAbstractionProvider } = opts;
  const { wallMaterials } = wallCatalogSettings;

  const wallBaseSections = new Array<WallAssemblySection>(wallPaths.length);
  const wallDatumSections = new Array<WallAssemblySection[]>(wallPaths.length);

  const remappedFinishOperators = remapWallAssemblyOperators(wallDatumAbstractionProvider, wallCore, mirror);
  const { componentInstanceChain } = level;
  /**
   * Collects the vertical datums for each finish layer. These are used to split the wall vertically
   * into sections.
   *
   * @TODO - this should be deleted once wallAssemblyLayers store a resolved number
   * rather than a datum reference
   */
  const getFinishLayerVerticalDatums = (
    leftWallSide: WallSideDescriptor,
    leftWallOperators: WallAssemblyOperator[],
    rightWallSide: WallSideDescriptor,
    rightWallOperators: WallAssemblyOperator[],
  ) => {
    const leftFinishDatums: LevelInches[] = [];
    leftWallOperators.forEach((operator) => {
      if (!isWallAssemblyLayerOperator(operator) || isWallAssemblyLayerRemoveOperator(operator)) {
        return;
      }
      const finishLayerSide = operator.layer.roomId === leftWallSide.wallRoom.room?.id ? leftWallSide : rightWallSide;
      const { lowerDatumUsage, upperDatumUsage } = operator.layer;

      if (lowerDatumUsage) {
        leftFinishDatums.push(
          wallDatumAbstractionProvider.resolveVerticalDatumRelativeToWallSide(
            lowerDatumUsage,
            finishLayerSide,
            "WALL_BOTTOM",
            componentInstanceChain,
          ),
        );
      }

      if (upperDatumUsage) {
        leftFinishDatums.push(
          wallDatumAbstractionProvider.resolveVerticalDatumRelativeToWallSide(
            upperDatumUsage,
            finishLayerSide,
            "WALL_TOP",
            componentInstanceChain,
          ),
        );
      }
    });

    const rightFinishDatums: LevelInches[] = [];
    rightWallOperators.forEach((operator) => {
      if (!isWallAssemblyLayerOperator(operator) || isWallAssemblyLayerRemoveOperator(operator)) {
        return;
      }
      const finishLayerSide = operator.layer.roomId === leftWallSide.wallRoom.room?.id ? leftWallSide : rightWallSide;
      const { lowerDatumUsage, upperDatumUsage } = operator.layer;

      if (lowerDatumUsage) {
        rightFinishDatums.push(
          wallDatumAbstractionProvider.resolveVerticalDatumRelativeToWallSide(
            lowerDatumUsage,
            finishLayerSide,
            "WALL_BOTTOM",
            componentInstanceChain,
          ),
        );
      }

      if (upperDatumUsage) {
        rightFinishDatums.push(
          wallDatumAbstractionProvider.resolveVerticalDatumRelativeToWallSide(
            upperDatumUsage,
            finishLayerSide,
            "WALL_TOP",
            componentInstanceChain,
          ),
        );
      }
    });

    return { leftFinishDatums, rightFinishDatums };
  };

  for (let halfEdge = 0; halfEdge < wallPaths.length; halfEdge++) {
    const wallPath = wallPaths[halfEdge];
    const wallCoreMass = wallCoreWalls[wallCore.halfEdgeToWall[halfEdge]];

    const { wallDescriptor } = wallPath;
    const { leftSide, rightSide } = wallDescriptor;

    const leftRemappedFinishOperators = remappedFinishOperators[halfEdge];
    const rightRemappedFinishOperators = remappedFinishOperators[planarGraphAdjacentHalfEdge(halfEdge)];

    const wallFinishDatums = getFinishLayerVerticalDatums(
      leftSide,
      leftRemappedFinishOperators,
      rightSide,
      rightRemappedFinishOperators,
    );

    const wallPathBaseSection = wallCoreSections.wallBaseSections[halfEdge];
    const wallPathCoreSections = wallCoreSections.wallDatumSections[halfEdge];
    const wallPathFinishSections = makeWallFinishSections(
      wallDescriptor,
      wallFinishDatums.leftFinishDatums,
      wallFinishDatums.rightFinishDatums,
    );

    wallBaseSections[halfEdge] = makeWallAssemblyFinishSection(
      level,
      wallDatumAbstractionProvider,
      wallPath,
      wallPathBaseSection,
      wallLayerWithFallback({
        wall: wallCoreMass,
        wallLayer: WallLayerType.SUBSTRATE,
        wallLOD: WallLOD.L1,
      }),
      [wallPathBaseSection],
      leftRemappedFinishOperators,
      rightRemappedFinishOperators,
      wallMaterials,
    );
    wallDatumSections[halfEdge] = wallPathFinishSections.map((finishSection) => {
      return makeWallAssemblyFinishSection(
        level,
        wallDatumAbstractionProvider,
        wallPath,
        finishSection,
        wallLayerWithFallback({
          wall: wallCoreMass,
          wallLayer: WallLayerType.SUBSTRATE,
          wallLOD: WallLOD.L2,
          cutPlane: (finishSection.bottomOffset + finishSection.topOffset) / 2,
        }),
        wallPathCoreSections,
        leftRemappedFinishOperators,
        rightRemappedFinishOperators,
        wallMaterials,
      );
    });

    adjustWallSectionAlignment(wallPath, [wallBaseSections[halfEdge]], opts.wallSettings);
    adjustWallSectionAlignment(wallPath, wallDatumSections[halfEdge], opts.wallSettings);
  }

  return {
    wallBaseSections,
    wallDatumSections,
  };
}

function adjustWallSectionAlignment(
  wallPath: WallPath,
  wallSections: WallAssemblySection[],
  wallSettings: WallSettings,
) {
  for (let i = wallSections.length - 1; i >= 0; i--) {
    const wallSectionAlignmentOffset = makeWallSectionAlignmentOffset(wallPath, wallSections, i, wallSettings);
    if (wallSectionAlignmentOffset !== 0) {
      const section = wallSections[i];
      for (const type in section.wallAssemblyOffsets) {
        section.wallAssemblyOffsets[type] = halfEdgeOffsetsOffset(
          section.wallAssemblyOffsets[type],
          wallSectionAlignmentOffset,
        );
      }
    }
  }
}

interface WallLayerDatumEndCapSources {
  readonly datum: LevelInches;
  readonly layer: WallLayerType;
  readonly sections: number[];
  readonly sources: WallEndCapSource[];
  readonly paths: WallPathBase[];
  readonly ends: WallEndCap[];
}

interface WallDatumEndCapSources {
  [datum: number]: WallLayerDatumEndCapSources;
}

function wallLayerDatumEndCapSourcesNew(
  datum: LevelInches,
  layer: WallLayerType,
  length: number,
): WallLayerDatumEndCapSources {
  return {
    datum,
    layer,
    sections: new Array(length),
    sources: new Array(length),
    paths: new Array(length),
    ends: new Array(length),
  };
}

function wallSectionIndexAtDatum(
  datum: LevelInches,
  halfEdgeIndex: number,
  wallPaths: WallPath[],
  wallDatumSections: WallSection[][],
  includeVoidSections = false,
) {
  let idx = wallDatumSections[halfEdgeIndex].findIndex(({ topOffset, bottomOffset }) => {
    return bottomOffset <= datum && datum < topOffset;
  });

  if (!includeVoidSections && idx >= 0) {
    const descriptor = wallPaths[halfEdgeIndex].wallDescriptor;
    const section = wallDatumSections[halfEdgeIndex][idx];
    if (isVoidWallSection(descriptor, section)) {
      idx = -1;
    }
  }
  return idx;
}

function wallSectionIndicesAtDatum(
  datum: LevelInches,
  halfEdges: number[],
  wallPaths: WallPath[],
  wallDatumSections: WallSection[][],
  includeVoidSections = false,
) {
  return halfEdges.reduce(
    (indices, halfEdgeIndex) => {
      indices[halfEdgeIndex] = wallSectionIndexAtDatum(
        datum,
        halfEdgeIndex,
        wallPaths,
        wallDatumSections,
        includeVoidSections,
      );
      return indices;
    },
    {} as { [halfEdgeIndex: number]: number },
  );
}

function wallSectionIndicesToWallPathBase(
  halfEdges: number[],
  wallSectionIndices: { [halfEdgeIndex: number]: number },
) {
  return halfEdges.reduce(
    (wallPaths, halfEdgeIndex) => {
      const wallType = wallSectionIndices[halfEdgeIndex] < 0 ? WallType.VOID : WallType.SOLID;
      wallPaths[halfEdgeIndex] = { wallType, halfEdgeIndex };
      return wallPaths;
    },
    {} as { [halfEdgeIndex: number]: WallPathBase },
  );
}

function maxFoundationDatumAtVertex(halfEdges: number[], wallPaths: WallPath[]) {
  let maxFoundationAtVertex: number | undefined;

  for (const he of halfEdges) {
    const { wallDescriptor } = wallPaths[he];
    const { leftSide, rightSide } = wallDescriptor;
    const maxFoundationAtPath = Math.max(leftSide.wallSideDatums.foundationTop, rightSide.wallSideDatums.foundationTop);
    maxFoundationAtVertex = Math.max(maxFoundationAtPath, maxFoundationAtVertex ?? maxFoundationAtPath);
  }

  assertIsNotUndefined(maxFoundationAtVertex);

  return levelInchesNew(maxFoundationAtVertex);
}

/**
 * @NOTE - this function currently only in use for end caps below foundation datums.
 * This will need to be expanded to create appropriate end caps for conditions above foundation datums
 *
 * @TODO - expand this function to work on more varied wall conditions. The offsetting currently assumes
 * 90 degree walls for calculating the appropriate distance to offset the end cap
 */
function vertexHalfEdgeEndCapOffset({
  datum,
  halfEdgeIndex,
  vertex,
  planarGraph,
  wallLayer,
  wallPaths,
  wallDatumSections,
  defaultWallCapLength,
}: {
  datum: LevelInches;
  halfEdgeIndex: number;
  vertex: number;
  planarGraph: PlanarGraph;
  wallLayer: WallLayerType;
  wallPaths: WallPath[];
  wallDatumSections: WallAssemblySection[][];
  defaultWallCapLength: number;
}) {
  const vHalfEdges = planarGraph.vertexHalfEdges[vertex];

  // in order to accurately detect end caps, we treat all walls as solid and detect the appropriate offsets
  const vDatumSectionIndices = wallSectionIndicesAtDatum(datum, vHalfEdges, wallPaths, wallDatumSections, true);

  const ohe = planarGraphAdjacentHalfEdge(halfEdgeIndex);
  const hePath = wallPaths[halfEdgeIndex];

  const [tx, ty] = line2Tangent(hePath.startX, hePath.startY, hePath.endX, hePath.endY);

  let vOffset: number | undefined;

  for (const vhe of vHalfEdges) {
    if (vhe === halfEdgeIndex || vhe === ohe) {
      continue;
    }

    if (vDatumSectionIndices[vhe] >= 0) {
      const vhePath = wallPaths[vhe];
      const vheAssembly = wallDatumSections[vhe][vDatumSectionIndices[vhe]];
      const { wallAssemblyOffsets } = vheAssembly;
      const { wallDescriptor: vheDescriptor } = vhePath;
      const { floorPriority: vheFloorPriority, leftSide: vheLeftSide, rightSide: vheRightSide } = vheDescriptor;

      let vheOffsets = wallAssemblyOffsets[wallLayer];
      let useClosestOffset = false;

      /**
       * @TODO - more miter control can be had here for end caps. Right now the default is BUTT against the
       * max foundation offset. However, some walls may want miter controls of foundation walls. That should happen here
       */
      if (wallAssemblyOffsets[WallLayerTypeAdditional.FOUNDATION]) {
        /**
         * This wall has a foundation component attached to it.
         *
         * The foundationTop datum will be different on the inside / outside of the wall if this is a slab with a stem wall
         * extending above it. If the datum we are generating is below the foundation datum on the EXTERIOR side of the wall,
         * then we need to use the foundation wall offsets for alignment, and project the wall end cap to the INTERIOR side of the wall
         *
         */
        const vheFoundationOffsets = wallAssemblyOffsets[WallLayerTypeAdditional.FOUNDATION];
        const vheDatums =
          vheFloorPriority === PathPriority.LEFT ? vheRightSide.wallSideDatums : vheLeftSide.wallSideDatums;
        if (vheDatums.foundationTop > datum) {
          useClosestOffset = true;
          vheOffsets = vheFoundationOffsets;
        }
      }

      const [nx, ny] = line2Normal(vhePath.startX, vhePath.startY, vhePath.endX, vhePath.endY);

      const dot = vec2Dot(tx, ty, nx, ny);
      const dotAbs = Math.abs(dot);
      if (dotAbs < ANGLE_EPSILON) {
        continue;
      }

      let pOffset: number;
      if (useClosestOffset) {
        pOffset = vheOffsets.leftOffset;
        if (dot > 0) {
          pOffset = vheOffsets.rightOffset;
        }
      } else {
        pOffset = -vheOffsets.rightOffset;
        if (dot > 0) {
          pOffset = -vheOffsets.leftOffset;
        }
      }

      // larger offset means a wall is closer
      if (vOffset === undefined || pOffset > vOffset) {
        vOffset = pOffset;
      }
    }
  }

  return vOffset ?? defaultWallCapLength;
}

function addVertexEndCapSourcesAtDatum({
  datumSourcesOut,
  datum,
  vertex,
  wallPaths,
  wallDatumSections,
  planarGraph,
  defaultWallCapLength,
}: {
  datumSourcesOut: WallLayerDatumEndCapSources;
  datum: LevelInches;
  vertex: number;
  planarGraph: PlanarGraph;
  wallPaths: WallPath[];
  wallDatumSections: WallAssemblySection[][];
  defaultWallCapLength: number;
}) {
  const { paths, sources, sections, layer } = datumSourcesOut;

  // find the sections for the half edges at this vertex that cut through the datum if existing,
  // and map them to a WallPathBase
  const vHalfEdges = planarGraph.vertexHalfEdges[vertex];
  const vDatumSectionIndices = wallSectionIndicesAtDatum(datum, vHalfEdges, wallPaths, wallDatumSections);
  const vDatumWallPaths = wallSectionIndicesToWallPathBase(vHalfEdges, vDatumSectionIndices);

  const vDatumFoundation = maxFoundationDatumAtVertex(vHalfEdges, wallPaths);
  const vDatumBelowFoundation = vDatumFoundation > datum;

  const wallMiterJoin: WallEndMiterJoin = {
    joinType: WallEndJoinType.MITER,
    shiftTJunction: true,
  };

  const wallFreeEndJoin: WallEndButtJoin = {
    joinType: WallEndJoinType.BUTT,
    buttCapMode: ButtCapMode.END_MITER_EXTEND,
    buttCapDefault: defaultWallCapLength,
    buttCapMaxPerpEdgeAligned: true,
  };

  const voidSectionOffsets: HalfEdgeOffsets = halfEdgeOffsetsNew(0, 0);

  // make end cap sources for this vertex/datum/layer
  const vDatumHalfEdgeSources = makeVertexHalfEdgeWallEndCapSources(
    {
      getWallPath: (halfEdge) => {
        return vDatumWallPaths[halfEdge];
      },
      getWallEndCapOffsets: (halfEdge) => {
        const sectionIndex = vDatumSectionIndices[halfEdge];
        if (sectionIndex < 0) {
          return voidSectionOffsets;
        }
        const sectionAssembly = wallDatumSections[halfEdge][sectionIndex];
        return sectionAssembly.wallAssemblyOffsets[layer];
      },
      /**
       * T junctions should use the same offset regardless of
       * datum. This is currently set to the baseOffset of the
       * halfEdge, which represents the bottom of wallFraming for
       * solidWalls
       */
      getWallTJunctionOffsets: (halfEdge) => {
        const baseWallPath = wallPaths[halfEdge];
        const baseOffset = levelInchesNew(baseWallPath.baseOffset);
        const baseSectionIndex = wallSectionIndexAtDatum(baseOffset, halfEdge, wallPaths, wallDatumSections);
        if (baseSectionIndex < 0) {
          return voidSectionOffsets;
        }
        const sectionAssembly = wallDatumSections[halfEdge][baseSectionIndex];
        return sectionAssembly.wallAssemblyOffsets[layer];
      },
      getWallMiterJoin: (_halfEdge) => {
        return wallMiterJoin;
      },
      getWallFreeEndJoin: (halfEdge) => {
        if (!vDatumBelowFoundation) {
          return wallFreeEndJoin;
        }
        return {
          joinType: WallEndJoinType.BUTT,
          buttCapMode: ButtCapMode.END_ALIGNED,
          buttCapWrap: 0,
          buttCapExtension: vertexHalfEdgeEndCapOffset({
            datum,
            halfEdgeIndex: halfEdge,
            vertex,
            planarGraph,
            wallLayer: layer,
            wallPaths,
            wallDatumSections,
            defaultWallCapLength,
          }),
        };
      },
    },
    planarGraph,
    vertex,
  );

  for (const source of vDatumHalfEdgeSources) {
    const { halfEdge } = source;

    sources[halfEdge] = source;
    paths[halfEdge] = vDatumWallPaths[halfEdge];
    sections[halfEdge] = vDatumSectionIndices[halfEdge];
  }
}

function addWallEndCapsToWallDatumEndCapSources({
  datumSourcesOut,
  wallDatumSections,
  planarGraph,
  planarGraphFacePoleOfInaccessibilityPositions,
}: {
  datumSourcesOut: WallDatumEndCapSources;
  wallPaths: WallPath[];
  wallDatumSections: WallAssemblySection[][];
  planarGraph: PlanarGraph;
  planarGraphFacePoleOfInaccessibilityPositions: PlanarGraphFacePoleOfInaccessibilityPositions;
}) {
  const wallDatumsSorted = Object.keys(datumSourcesOut)
    .map((d) => +d)
    .sort((a, b) => a - b);

  // repair and create wallEndCaps. We do this per face so that we can ignore faces
  // that do not have any vertices at a particular datum.
  for (let f = 0, l = planarGraph.faceHalfEdges.length; f < l; f++) {
    const fHalfEdges = planarGraph.faceHalfEdges[f];
    const facePoleOfInaccessibilityPositionForRepair = planarGraphFacePoleOfInaccessibilityPositions.facePositions[f];

    for (const datum of wallDatumsSorted) {
      const { sources, sections, ends } = datumSourcesOut[datum];

      const fHalfEdgeEndCapSources: WallEndCapSource[] = [];
      const fHalfEdgesAtDatum: number[] = [];

      const addHalfEdge = (halfEdge: number) => {
        const halfEdgeEndCapSource = sources[halfEdge];
        if (halfEdgeEndCapSource) {
          fHalfEdgeEndCapSources[halfEdge] = halfEdgeEndCapSource;
          fHalfEdgesAtDatum.push(halfEdge);
          return;
        }

        // this half edge does not have a wall end cap source at this datum.
        // This can happen for one of two reasons:

        // - the edge has no sections at this datum. In this case,
        // we need to generate a NO_OP miter for this half edge
        const halfEdgeSectionIndex = sections[halfEdge];
        if (halfEdgeSectionIndex === undefined || halfEdgeSectionIndex < 0) {
          const [sx, sy] = planarGraphHalfEdgeStart(planarGraph, halfEdge);
          fHalfEdgeEndCapSources[halfEdge] = {
            halfEdge: halfEdge,
            sourceType: EndCapSourceType.NO_OP,
            sx,
            sy,
          };
          return;
        }

        // - the edge has a section at this datum, but it has no condition change between the
        // section base offset and the current datum. In this case, we fall back to
        // the section datum for the end cap source. Since we are creating these
        // end caps in sorted vertical order, the section datum will already have been repaired
        const halfEdgeSection = wallDatumSections[halfEdge][halfEdgeSectionIndex];
        const halfEdgeSectionDatumSources = datumSourcesOut[halfEdgeSection.bottomOffset];
        const halfEdgeSectionEndCapSource = halfEdgeSectionDatumSources.sources[halfEdge];
        fHalfEdgeEndCapSources[halfEdge] = halfEdgeSectionEndCapSource;
      };

      for (const fhe of fHalfEdges) {
        // repairing miters requires both end cap sources on a given edge.
        const ohe = planarGraphAdjacentHalfEdge(fhe);
        addHalfEdge(fhe);
        addHalfEdge(ohe);
      }

      // skip faces not on this datum
      if (!fHalfEdgesAtDatum.length) {
        continue;
      }

      const fHalfEdgeEndCaps = makeFaceWallEndCaps({
        face: f,
        planarGraph,
        wallEndCapSources: fHalfEdgeEndCapSources,
        facePoleOfInaccessibilityPositionForRepair,
      });

      for (const fhe of fHalfEdgesAtDatum) {
        ends[fhe] = fHalfEdgeEndCaps[fhe];
        sources[fhe] = fHalfEdgeEndCapSources[fhe];
      }
    }
  }
}

function makeWallDatumEndCapSources({
  wallPaths,
  wallDatumSections,
  wallLayer,
  planarGraph,
  planarGraphFacePoleOfInaccessibilityPositions,
  defaultWallCapLength,
}: {
  wallPaths: WallPath[];
  wallDatumSections: WallAssemblySection[][];
  wallLayer: WallLayerType;
  planarGraph: PlanarGraph;
  planarGraphFacePoleOfInaccessibilityPositions: PlanarGraphFacePoleOfInaccessibilityPositions;
  defaultWallCapLength: number;
}) {
  const wallDatumEndCapSources: WallDatumEndCapSources = {};

  // for each vertex on the planar graph, we build a set of vertical wall end cap sources
  // specific to that vertex for all vertical wall condition changes.
  // This prevents unnecessary cuts through the building
  for (let v = 0, l = planarGraph.vertexHalfEdges.length; v < l; v++) {
    const vHalfEdges = planarGraph.vertexHalfEdges[v];

    // collect the wall sections and datum changes
    // from all half edges emanating from this vertex
    const vHalfEdgeDatums = vHalfEdges.reduce((datums, vhe) => {
      wallDatumSections[vhe].forEach(({ bottomOffset, topOffset }) => {
        datums.add(bottomOffset);
        datums.add(topOffset);
      });
      return datums;
    }, new Set<LevelInches>());

    // create wall end cap sources for each vertical condition change at this vertex
    for (const vDatum of vHalfEdgeDatums) {
      // init datum if not existing for another vertex
      if (!wallDatumEndCapSources[vDatum]) {
        wallDatumEndCapSources[vDatum] = wallLayerDatumEndCapSourcesNew(
          vDatum,
          wallLayer,
          planarGraph.halfEdges.length,
        );
      }

      addVertexEndCapSourcesAtDatum({
        datumSourcesOut: wallDatumEndCapSources[vDatum],
        datum: vDatum,
        vertex: v,
        planarGraph,
        wallPaths,
        wallDatumSections,
        defaultWallCapLength,
      });
    }
  }

  addWallEndCapsToWallDatumEndCapSources({
    datumSourcesOut: wallDatumEndCapSources,
    wallPaths,
    wallDatumSections,
    planarGraph,
    planarGraphFacePoleOfInaccessibilityPositions,
  });

  return wallDatumEndCapSources;
}

function makeWallLayerEndCaps({
  wallPaths,
  wallSections,
  wallLayer,
  planarGraph,
  planarGraphFacePoleOfInaccessibilityPositions,
  defaultWallCapLength,
}: {
  wallPaths: WallPath[];
  wallSections: WallAssemblySection[];
  wallLayer: WallLayerType;
  planarGraph: PlanarGraph;
  planarGraphFacePoleOfInaccessibilityPositions: PlanarGraphFacePoleOfInaccessibilityPositions;
  defaultWallCapLength: number;
}) {
  const wallFreeEndJoin: WallEndButtJoin = {
    joinType: WallEndJoinType.BUTT,
    buttCapMode: ButtCapMode.END_MITER_EXTEND,
    buttCapDefault: defaultWallCapLength,
    buttCapMaxPerpEdgeAligned: true,
  };
  const wallMiterJoin: WallEndMiterJoin = {
    joinType: WallEndJoinType.MITER,
    shiftTJunction: true,
  };
  const wallEndCapSources = makeWallEndCapSources(
    {
      getWallPath: (halfEdge) => {
        return wallPaths[halfEdge];
      },
      getWallEndCapOffsets: (halfEdge) => {
        return wallSections[halfEdge].wallAssemblyOffsets[wallLayer];
      },
      getWallTJunctionOffsets: (halfEdge) => {
        return wallSections[halfEdge].wallAssemblyOffsets[wallLayer];
      },
      getWallMiterJoin: (_halfEdge) => {
        return wallMiterJoin;
      },
      getWallFreeEndJoin: (_halfEdge) => {
        return wallFreeEndJoin;
      },
    },
    planarGraph,
  );
  const wallEndCaps = makeWallEndCaps({
    wallEndCapSources,
    planarGraph,
    planarGraphFacePoleOfInaccessibilityPositions,
    useRepairMitersOnSelfIntersectingRoomEdges: true,
  });
  return wallEndCaps;
}

/**
 * splitWallLayerAtDatums takes a generated `WallLayer` and splits it vertically
 * at the given datums from `wallDatumSections`.
 * @param wallLayer the layer to split
 * @param wallDatumSections the vertical sections to split this layer with
 * @param wallDatumEndCapSources the provided endCaps at condition changes for the sections
 *
 * In the event that there are missing endCapSources at a given datum, the previous lower endCap
 * will be used.
 */
function splitWallLayerAtDatums({
  wallLayer,
  wallDatumSections,
  wallDatumEndCapSources,
}: {
  wallLayer: WallLayer;
  wallDatumSections: WallAssemblySection[];
  wallDatumEndCapSources: WallDatumEndCapSources;
}) {
  const { id, wallLayerType, path, floorElevation } = wallLayer;
  const { halfEdgeIndex } = path;
  const oppHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);

  const layers: WallLayer[] = [];
  const wallDatumsSorted = Object.keys(wallDatumEndCapSources)
    .map((d) => levelInchesNew(+d))
    .sort((a, b) => a - b);

  for (let i = 0; i < wallDatumSections.length; i++) {
    const wallSection = wallDatumSections[i];
    const wallSectionAssembly = wallSection.wallAssembly;
    const wallSectionOffsets = wallSection.wallAssemblyOffsets[wallLayerType];

    if (isVoidWallSection(path.wallDescriptor, wallSection)) {
      layers.push(
        wallLayerNew(
          wallLayerType,
          voidWallFromHalfEdgeOffsets(id, path, wallSectionAssembly, floorElevation, wallSection, wallSectionOffsets),
        ),
      );
      continue;
    }

    const wallSectionSources = wallDatumEndCapSources[wallSection.bottomOffset];
    const wallSectionCaps = wallSectionSources.ends;

    // we only generate caps on the side that has the condition change
    // in the event that there is only an end cap on one side, we fall back to the
    // last section end cap on the opposite side
    const wallSectionStartCap = wallSectionCaps[halfEdgeIndex];
    const wallSectionEndCap = wallSectionCaps[oppHalfEdgeIndex];
    const wallSectionDatums = wallDatumsSorted.filter(
      (d) =>
        d >= wallSection.bottomOffset &&
        d <= wallSection.topOffset &&
        (!!wallDatumEndCapSources[d].sources[halfEdgeIndex] || !!wallDatumEndCapSources[d].sources[oppHalfEdgeIndex]),
    );

    let wallDatumSectionStartCap = wallSectionStartCap;
    let wallDatumSectionEndCap = wallSectionEndCap;

    for (let j = 0; j < wallSectionDatums.length - 1; j++) {
      const wallSectionDatum = wallSectionDatums[j];
      const wallSectionDatumSources = wallDatumEndCapSources[wallSectionDatum];
      const wallSectionDatumCaps = wallSectionDatumSources.ends;

      wallDatumSectionStartCap = wallSectionDatumCaps[halfEdgeIndex] ?? wallDatumSectionStartCap;
      wallDatumSectionEndCap = wallSectionDatumCaps[oppHalfEdgeIndex] ?? wallDatumSectionEndCap;

      // use the new top and bottom offsets, but keep the rest of the section the same
      const wallDatumSection: WallSection = {
        ...wallSection,
        bottomOffset: wallSectionDatum,
        topOffset: wallSectionDatums[j + 1],
      };

      if (wallDatumSection.topOffset - wallDatumSection.bottomOffset < DIST_EPSILON) {
        continue;
      }

      // make sure this is a solid wall path, since we may be adding solid
      // sections to a void wall
      const wallDatumPath: WallPath = {
        ...path,
        wallType: WallType.SOLID,
      };

      layers.push(
        wallLayerNew(
          wallLayerType,
          solidWallMassFromWallEnds(
            id,
            "WALL_LAYER",
            wallDatumPath,
            wallSectionAssembly,
            floorElevation,
            wallDatumSectionStartCap,
            wallDatumSectionEndCap,
            wallDatumSection,
          ),
        ),
      );
    }
  }

  return layers;
}

interface MakeWallMassOptions {
  wallPaths: WallPath[];
  wallLayer: WallLayerType;
  wallSections: WallAssemblySection[];
  wallEndCaps: WallEndCap[];
  wallLevelFloorElevation: number;
}

function makeWallMasses({
  wallPaths,
  wallLayer,
  wallSections,
  wallEndCaps,
  wallLevelFloorElevation,
}: MakeWallMassOptions) {
  const wallMasses: RWTable<WallLayer> = {};
  const halfEdgeToWallMass = new Array<string>(wallPaths.length);

  const visited = new Array<boolean>(wallPaths.length);

  for (let halfEdge = 0, l = wallPaths.length; halfEdge < l; halfEdge++) {
    const path = wallPaths[halfEdge];
    if (visited[halfEdge]) {
      continue;
    }

    const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdge);

    visited[halfEdge] = true;
    visited[oppHalfEdge] = true;

    const wallId = uid();
    const wallSection = wallSections[halfEdge];

    let wallMass: WallMass;
    if (path.wallType === WallType.VOID) {
      wallMass = voidWallFromHalfEdgeOffsets(
        wallId,
        path,
        wallSection.wallAssembly,
        wallLevelFloorElevation,
        wallSection,
        wallSection.wallAssemblyOffsets[wallLayer],
      );
    } else {
      wallMass = solidWallMassFromWallEnds(
        wallId,
        "WALL",
        path,
        wallSection.wallAssembly,
        wallLevelFloorElevation,
        wallEndCaps[halfEdge],
        wallEndCaps[oppHalfEdge],
        wallSection,
      );
    }

    halfEdgeToWallMass[halfEdge] = wallMass.id;
    halfEdgeToWallMass[oppHalfEdge] = wallMass.id;

    wallMasses[wallMass.id] = wallLayerNew(wallLayer, wallMass);
  }

  return { wallMasses, halfEdgeToWallMass };
}

interface MakeWallLayerMassesOptions {
  wallPaths: WallPath[];
  wallLayer: WallLayerType;
  wallMassSystemSections: WallMassSystemSections;
  wallLevelFloorElevation: number;
  planarGraph: PlanarGraph;
  planarGraphFacePoleOfInaccessibilityPositions: PlanarGraphFacePoleOfInaccessibilityPositions;
  defaultWallCapLength: number;
}

function makeWallLayerMasses({
  wallPaths,
  wallLayer,
  wallMassSystemSections,
  wallLevelFloorElevation,
  planarGraph,
  planarGraphFacePoleOfInaccessibilityPositions,
  defaultWallCapLength,
}: MakeWallLayerMassesOptions) {
  const { wallBaseSections: wallSections, wallDatumSections } = wallMassSystemSections;

  assertIsNumber(defaultWallCapLength);

  const wallEndCaps = makeWallLayerEndCaps({
    wallPaths,
    wallLayer,
    wallSections,
    planarGraph,
    planarGraphFacePoleOfInaccessibilityPositions,
    defaultWallCapLength,
  });

  const wallDatumEndCapSources = makeWallDatumEndCapSources({
    wallPaths,
    wallLayer,
    wallDatumSections,
    planarGraph,
    planarGraphFacePoleOfInaccessibilityPositions,
    defaultWallCapLength,
  });

  const wallMasses = makeWallMasses({
    wallPaths,
    wallLayer,
    wallSections,
    wallEndCaps,
    wallLevelFloorElevation,
  });

  for (const wallId in wallMasses.wallMasses) {
    const wallLayerMass = wallMasses.wallMasses[wallId];
    const wallLayerDatums = wallDatumSections[wallLayerMass.path.halfEdgeIndex];
    wallLayerMass.layers.push(
      ...splitWallLayerAtDatums({
        wallLayer: wallLayerMass,
        wallDatumSections: wallLayerDatums,
        wallDatumEndCapSources,
      }),
    );
  }

  return wallMasses;
}

enum RoomSide {
  INSIDE,
  OUTSIDE,
}

function wallLayerDomainEdge(
  wallIndex: WallIndex,
  wall: Wall,
  room: Room,
  roomSide: RoomSide,
  wallLayer: WallLayerType,
  wallLOD: WallLOD,
): PolyLine2 | false {
  const wallStrip = wallIndex.parentWallStrip(room, wall);
  if (!wallStrip) {
    return false;
  }

  const walls = wallStrip.toWallArray();
  if (!walls.length) {
    return false;
  }

  const getWallStripAlignedPath = (wallStripWall: Wall) => {
    const wallStripPath = getWallPath(wallStripWall);
    const wallStripRoomAligned = wallStripPath.leftRoom === room;
    const wallStipAlignedWallPathPath = wallStripRoomAligned ? wallStripPath : wallPathOpposite(wallStripPath);
    return polyLine2New([
      wallStipAlignedWallPathPath.startX,
      wallStipAlignedWallPathPath.startY,
      wallStipAlignedWallPathPath.endX,
      wallStipAlignedWallPathPath.endY,
    ]);
  };

  const getWallStripAlignedEdge = (wallMass: SolidWallMass) => {
    const wallStripRoomAligned = wallMass.path.leftRoom === room;
    const wallOuterEdge = wallStripRoomAligned ? wallMass.rightEdge : wallMass.leftEdge;
    const wallInnerEdge = wallStripRoomAligned ? wallMass.leftEdge : wallMass.rightEdge;
    return roomSide === RoomSide.OUTSIDE ? wallOuterEdge : wallInnerEdge;
  };

  const wallPaths = walls.map(getWallStripAlignedPath);
  const wallPathFirst = wallPaths[0];
  const wallPathLast = wallPaths[wallPaths.length - 1];
  const baseLine = polyLine2New([wallPathFirst[0], wallPathFirst[1], wallPathLast[2], wallPathLast[3]]);
  let minT = 1;
  let maxT = 0;

  for (const wall of walls) {
    const wallMass = wall.wallType === WallType.SOLID ? wall.mass : wall;
    const wallLayers = wallMassLayers(wallMass, wallLayer, wallLOD);
    for (const wallLayerMass of wallLayers) {
      if (wallLayerMass.wallType === WallType.VOID) {
        continue;
      }
      const edge = getWallStripAlignedEdge(wallLayerMass);
      const { sT, eT } = wallSideEdgeDomain(baseLine, edge);
      minT = Math.min(minT, sT, eT);
      maxT = Math.max(maxT, sT, eT);
    }
  }

  const [sx, sy] = line2Eval(baseLine[0], baseLine[1], baseLine[2], baseLine[3], minT, true);
  const [ex, ey] = line2Eval(baseLine[0], baseLine[1], baseLine[2], baseLine[3], maxT, true);

  return polyLine2New([sx, sy, ex, ey]);
}

function wallSideEdgeDomain(path: PolyLine2, pathAlignedEdge: PolyLine2) {
  const sT = closestParamNormalizedLine2(
    path[0],
    path[1],
    path[2],
    path[3],
    pathAlignedEdge[0],
    pathAlignedEdge[1],
    false,
  );
  const eT = closestParamNormalizedLine2(
    path[0],
    path[1],
    path[2],
    path[3],
    pathAlignedEdge[2],
    pathAlignedEdge[3],
    false,
  );
  return { sT, eT };
}

function remapWallLayerDomain(
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wall: Wall,
  wallIndex: WallIndex,
  wallAssemblyLayerOperators: WallAssemblyOperator[],
  isLeftSide: boolean,
  mirror: boolean,
) {
  const remapLayer = <T extends Partial<WallAssemblyLayer>>(
    wallSideAlignedWallPath: PolyLine2,
    wallSideStripDomainEdge: PolyLine2,
    wallSideLayer: T,
    effectiveMirror: boolean,
  ): T => {
    const [startX, startY, endX, endY] = wallSideStripDomainEdge;
    const wallStripLen = vec2Dist(startX, startY, endX, endY);

    const {
      startDatumUsage = defaultWallAssemblyLayerDomain.startDatumUsage,
      endDatumUsage = defaultWallAssemblyLayerDomain.endDatumUsage,
      lowerDatumUsage = defaultWallAssemblyLayerDomain.lowerDatumUsage,
      upperDatumUsage = defaultWallAssemblyLayerDomain.upperDatumUsage,
    } = wallSideLayer;

    const startDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(
      startDatumUsage,
      "WALL_START",
    );
    const endDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(
      endDatumUsage,
      "WALL_END",
    );

    // remap datums from wall strip to wall path
    let facadeStartOffset =
      startDatumAbstraction.resolveHorizontalDatum(wallStripLen, startDatumUsage.offset) / wallStripLen;
    let facadeEndOffset = endDatumAbstraction.resolveHorizontalDatum(wallStripLen, endDatumUsage.offset) / wallStripLen;

    if (effectiveMirror) {
      const t = 1 - facadeStartOffset;
      facadeStartOffset = 1 - facadeEndOffset;
      facadeEndOffset = t;
    }

    const [fsx, fsy] = line2Eval(startX, startY, endX, endY, facadeStartOffset, true);
    const [fex, fey] = line2Eval(startX, startY, endX, endY, facadeEndOffset, true);

    const facadeEdge = polyLine2New([fsx, fsy, fex, fey]);
    const facadeWallPathDomain = wallSideEdgeDomain(wallSideAlignedWallPath, facadeEdge);

    const { sT, eT } = facadeWallPathDomain;

    const remappedStartDatum = wallAssemblyHorizontalDatumNew(
      `Remapped ${startDatumAbstraction.getName()}`,
      WallHorizontalDatum.OUTSIDE_OF_SURFACE,
      HorizontalJustification.START_TO_END,
      sT,
      true,
    );

    const remappedEndDatum = wallAssemblyHorizontalDatumNew(
      `Remapped ${endDatumAbstraction.getName()}`,
      WallHorizontalDatum.OUTSIDE_OF_SURFACE,
      HorizontalJustification.START_TO_END,
      eT,
      true,
    );

    wallDatumAbstractionProvider.addDatumEntity(remappedStartDatum);
    wallDatumAbstractionProvider.addDatumEntity(remappedEndDatum);

    // We use the remapped datums for the start and end datum usages
    const newLayer: typeof wallSideLayer = {
      ...wallSideLayer,
      startDatumUsage: datumUsageNew(DatumHorizontalUsageType.WALL_HORIZONTAL, remappedStartDatum.id),
      endDatumUsage: datumUsageNew(DatumHorizontalUsageType.WALL_HORIZONTAL, remappedEndDatum.id),
      lowerDatumUsage,
      upperDatumUsage,
    };

    return newLayer;
  };

  const remappedOperators: WallAssemblyOperator[] = [];

  const wallPath = getWallPath(wall);
  const { insideRoom, outsideRoom } = getWallSideRooms(wall, isLeftSide);

  const wallLayerDomain = WallLayerType.SUBSTRATE; // finish layers are aligned to substrate domain
  const wallFinishLOD = WallLOD.L2;

  for (const layerOperator of wallAssemblyLayerOperators) {
    if (!isWallAssemblyLayerOperator(layerOperator) || isWallAssemblyLayerRemoveOperator(layerOperator)) {
      remappedOperators.push(layerOperator);
      continue;
    }
    const outside = layerOperator.layer.roomId === outsideRoom?.id;
    const wallRoom = outside ? outsideRoom : insideRoom;
    const wallRoomSide = outside ? RoomSide.OUTSIDE : RoomSide.INSIDE;
    const wallEdge = outside
      ? polyLine2New([wallPath.startX, wallPath.startY, wallPath.endX, wallPath.endY])
      : polyLine2New([wallPath.endX, wallPath.endY, wallPath.startX, wallPath.startY]);

    assertIsNotUndefined(wallRoom);
    const wallRoomSideLayerDomainEdge = wallLayerDomainEdge(
      wallIndex,
      wall,
      wallRoom,
      wallRoomSide,
      wallLayerDomain,
      wallFinishLOD,
    );

    if (wallRoomSideLayerDomainEdge) {
      if (!outside) {
        polyLine2Reverse(wallRoomSideLayerDomainEdge);
      }

      if (outside) {
        if (isLeftSide) {
          polyLine2Reverse(wallEdge);
        }
      } else {
        if (!isLeftSide) {
          polyLine2Reverse(wallEdge);
        }
      }

      const effectiveMirror = isCombinedGlobalInstanceMirror(mirror, wallRoom);
      const layer = remapLayer(wallEdge, wallRoomSideLayerDomainEdge, layerOperator.layer, effectiveMirror);
      remappedOperators.push({ ...layerOperator, layer } as WallAssemblyOperator);
    }
  }

  return remappedOperators;
}

export function makeWallMassSystem(opts: MakeWallMassSystemOptions): WallMassSystem {
  const {
    wallSettings,
    planarGraph: polygonPlanarGraph,
    roomLevelIndices,
    roomConnections,
    rooms,
    levelId,
    levels,
    wallPaths,
  } = opts;
  const { planarGraph } = polygonPlanarGraph;
  const { interiorWallOffset } = wallSettings;
  const wallLevelFloorElevation = levels[levelId].floorElevation;
  const level = levels[levelId];

  // this is a heavy calculation, so we do it once for all the wall generation
  const planarGraphFacePoleOfInaccessibilityPositions = planarGraphMakeFacePoleOfInaccessibilityPositions(planarGraph);

  // wall sections for the "core" wall masses (STRUCTURE and SUBSTRATE for now)
  const wallCoreSections = makeWallAssemblyCoreSections(level, wallPaths, opts);

  // Generate the "core" wall masses (LOD1 and LOD2)
  // These are used for generating a "core" wallMassSystem, which is used to
  // position finish layers using the wallStrip (room edge) domain of the substrate or structural layers.
  // from the room that created the facade.
  //
  // Currently, all facade layers use SUBSTRATE, but that can be parameterized.

  const wallStructure = makeWallLayerMasses({
    wallPaths,
    wallLayer: WallLayerType.STRUCTURE,
    wallMassSystemSections: wallCoreSections,
    wallLevelFloorElevation,
    planarGraph,
    planarGraphFacePoleOfInaccessibilityPositions,
    defaultWallCapLength: interiorWallOffset,
  });

  const wallSubstrate = makeWallLayerMasses({
    wallPaths,
    wallLayer: WallLayerType.SUBSTRATE,
    wallMassSystemSections: wallCoreSections,
    wallLevelFloorElevation,
    planarGraph,
    planarGraphFacePoleOfInaccessibilityPositions,
    defaultWallCapLength: interiorWallOffset,
  });

  // Generate the "core" wallMassSystem. This is the object that will be returned
  // by this function. We generate it without the finish layers, and then
  // use it to position finish layers

  const wallMasses: RWTable<WallMass> = {};
  const halfEdgeToWallMass = new Array<string>(wallPaths.length);

  for (const id in wallSubstrate.wallMasses) {
    const wallSubstrateLayer = wallSubstrate.wallMasses[id];
    const wallStructureLayer =
      wallStructure.wallMasses[wallStructure.halfEdgeToWallMass[wallSubstrateLayer.path.halfEdgeIndex]];

    const halfEdge = wallSubstrateLayer.path.halfEdgeIndex;
    const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdge);

    const wallId = uid();
    const wallMass: WallMass = {
      id: wallId, // this is reported by strict null checks to be a duplicate id, so the next line will be set to any to temporarily fix this
      ...(wallSubstrateLayer as any),
      layers: [wallStructureLayer, wallSubstrateLayer],
    };

    halfEdgeToWallMass[halfEdge] = wallMass.id;
    halfEdgeToWallMass[oppHalfEdge] = wallMass.id;

    wallMasses[wallMass.id] = wallMass;
  }

  const wallIndex = new WallIndexImpl(wallMasses, halfEdgeToWallMass, roomLevelIndices, polygonPlanarGraph);
  const wallMassSystem: WallMassSystem = {
    wallIndex,
    walls: wallMasses,
    halfEdgeToWall: halfEdgeToWallMass,
    openConnectedRoomSets: getOpenConnectedRoomSets(rooms, roomConnections, roomLevelIndices),
  };

  // Generate the wallFinish Layers using the "core" wallMassSystem
  const wallFinishSections = makeWallAssemblyFinishSections(level, wallPaths, wallMassSystem, wallCoreSections, opts);

  // Generate the wallFinish masses by applying the new wallFinishLayers
  // onto the "core" wallMasses

  const wallFinish = makeWallLayerMasses({
    wallPaths,
    wallLayer: WallLayerType.FINISH,
    wallMassSystemSections: wallFinishSections,
    wallLevelFloorElevation,
    planarGraph,
    planarGraphFacePoleOfInaccessibilityPositions,
    defaultWallCapLength: interiorWallOffset,
  });

  for (const id in wallMasses) {
    const wallMass = wallMasses[id];
    const wallFinishLayer = wallFinish.wallMasses[wallFinish.halfEdgeToWallMass[wallMass.path.halfEdgeIndex]];
    wallMass.layers.push(wallFinishLayer);
  }

  return wallMassSystem;
}

/**
 * makeWallMassSystemCustom takes a WallSystem and converts walls to SOLID_MASS or VOID.
 *
 * Wall widths for void walls are determined by either the interior/exterior thickness, or the room
 * edge offset.
 *
 * @NOTE - Walls created using this function will be stripped of any layer information
 */
export function makeWallMassSystemCustom({
  planarGraph,
  wallSystem,
  wallSettings,
  wallPredicate,
  wallOffset,
  wallPathRemapFunc,
  wallButtCapMode = ButtCapMode.END_ALIGNED,
  shiftCollinearTJunctions = true,
}: {
  planarGraph: PlanarGraph;
  wallSystem: WallSystem;
  wallSettings: WallSettings;
  wallPredicate: (wall: Wall) => boolean;
  wallOffset: (wall: Wall) => { leftOffset: number; rightOffset: number };
  wallPathRemapFunc?: (wallPath: WallPath) => WallPath;
  wallButtCapMode?: ButtCapMode;
  shiftCollinearTJunctions?: boolean;
}): WallMassSystem {
  const halfEdgeToOriginalWall = (halfEdgeIndex: number) => {
    return wallSystem.walls[wallSystem.wallIndex.halfEdgeToWall[halfEdgeIndex]];
  };

  const halfEdgeWallPathBaseOpposite = (path: WallPathBase): WallPathBase => {
    return {
      halfEdgeIndex: planarGraphAdjacentHalfEdge(path.halfEdgeIndex),
      wallType: path.wallType,
    };
  };

  // flip solid/void walls
  const offsets: HalfEdgeOffsets[] = [];
  const paths: WallPathBase[] = [];

  for (const wallId in wallSystem.walls) {
    const wall = wallSystem.walls[wallId];

    const he = getWallPath(wall).halfEdgeIndex;
    const ahe = planarGraphAdjacentHalfEdge(he);

    const wallType = wallPredicate(wall) ? WallType.SOLID_MASS : WallType.VOID;

    const wallOffsets = wallOffset(wall);
    const wallThickness = wallOffsets.leftOffset + wallOffsets.rightOffset;

    const path = { halfEdgeIndex: he, wallType };
    const offset = { ...wallOffsets, wallThickness };

    offsets[he] = offset;
    offsets[ahe] = halfEdgeOffsetsOpposite(offset);

    paths[he] = path;
    paths[ahe] = halfEdgeWallPathBaseOpposite(path);
  }

  const wallFreeEndJoin: WallEndButtJoin =
    wallButtCapMode === ButtCapMode.END_ALIGNED
      ? {
          joinType: WallEndJoinType.BUTT,
          buttCapMode: ButtCapMode.END_ALIGNED,
          buttCapWrap: 0,
          buttCapExtension: wallSettings.exteriorWallOffset,
        }
      : {
          joinType: WallEndJoinType.BUTT,
          buttCapMode: ButtCapMode.END_MITER_EXTEND,
          buttCapMaxPerpEdgeAligned: false,
          buttCapDefault: 0,
        };

  const wallMiterJoin: WallEndMiterJoin = {
    joinType: WallEndJoinType.MITER,
    shiftTJunction: shiftCollinearTJunctions,
  };

  // new wall endcaps from offset info
  const sources = makeWallEndCapSources(
    {
      getWallPath: (halfEdge) => {
        return paths[halfEdge];
      },
      getWallEndCapOffsets: (halfEdge) => {
        return offsets[halfEdge];
      },
      getWallTJunctionOffsets: (halfEdge) => {
        return offsets[halfEdge];
      },
      getWallMiterJoin: (_halfEdge) => {
        return wallMiterJoin;
      },
      getWallFreeEndJoin: (_halfEdge) => {
        return wallFreeEndJoin;
      },
    },
    planarGraph,
  );
  const caps = makeWallEndCaps({
    wallEndCapSources: sources,
    planarGraph,
  });

  const masses: { [massId: string]: SolidWallMass | VoidWall } = {};
  const visited = new Array<boolean>(paths.length);
  const halfEdgeToWallMass = new Array<string>(paths.length);

  for (let halfEdge = 0, l = paths.length; halfEdge < l; halfEdge++) {
    const path = paths[halfEdge];
    if (visited[halfEdge]) {
      continue;
    }

    const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdge);

    visited[halfEdge] = true;
    visited[oppHalfEdge] = true;

    let wall: SolidWallMass | VoidWall;

    const originalWall = halfEdgeToOriginalWall(halfEdge);
    const originalPath = getWallPath(originalWall);

    const newPath = wallPathRemapFunc ? wallPathRemapFunc(originalPath) : originalPath;
    const newSection = makeWallBaseSection(newPath.wallDescriptor);

    let floorElevation = 0;
    let newAssembly: WallAssembly;
    if (originalWall.wallType === WallType.SOLID) {
      floorElevation = originalWall.mass.floorElevation;
      newAssembly = originalWall.mass.wallAssembly;
    } else {
      floorElevation = originalWall.floorElevation;
      newAssembly = originalWall.wallAssembly;
    }

    const id = originalWall.id;
    const info = offsets[halfEdge];

    if (path.wallType === WallType.VOID) {
      wall = voidWallFromHalfEdgeOffsets(id, newPath, newAssembly, floorElevation, newSection, info);
    } else {
      wall = solidWallMassFromWallEnds(
        id,
        "WALL",
        newPath,
        newAssembly,
        floorElevation,
        caps[halfEdge],
        caps[oppHalfEdge],
        newSection,
      );
    }

    halfEdgeToWallMass[halfEdge] = wall.id;
    halfEdgeToWallMass[oppHalfEdge] = wall.id;

    masses[wall.id] = wall;
  }

  return {
    wallIndex: new WallIndexImpl(
      masses,
      halfEdgeToWallMass,
      wallSystem.wallIndex.roomLevelIndices,
      wallSystem.wallIndex.planarGraph,
    ),
    walls: masses,
    halfEdgeToWall: halfEdgeToWallMass,
    openConnectedRoomSets: [], //getOpenConnectedRoomSets(rooms, roomConnections, roomLevelIndices),
  };
}

interface MakeWallSystemOptions {
  wallDatumAbstractionProvider: WallDatumAbstractionProvider;
  wallMasses: Table<WallMassSystem>;
  halfEdgeToDoorSet?: HalfEdgeToDoorSet;
  halfEdgeToWindowSet?: HalfEdgeToWindowSet;
  halfEdgeToWallCutSet?: HalfEdgeToWallCutSet;
  wallCatalogSettings: WallCatalogSettings;
  levelId: string;
  mirror: boolean;
  issues: BuildingGenerationIssue[];
}

export function makeWallSystem(opts: MakeWallSystemOptions): WallSystem {
  const {
    wallDatumAbstractionProvider,
    wallMasses,
    halfEdgeToWindowSet = {},
    halfEdgeToDoorSet = {},
    halfEdgeToWallCutSet = {},
    wallCatalogSettings,
    levelId,
    mirror,
    issues,
  } = opts;

  const { wallMaterials } = wallCatalogSettings;

  const wallMassSystem = wallMasses[levelId];
  const { walls: masses, halfEdgeToWall, wallIndex } = wallMasses[levelId];

  const walls: Mutable<WallTable> = {};

  // Room openings never key to FREEFORM_WALL_HALF_EDGE_INDEX; attach cuts by geometry.
  const freeformOpeningCutsByMassId = collectFreeformWallOpeningCuts({
    windows: Object.values(halfEdgeToWindowSet).flat(),
    doors: Object.values(halfEdgeToDoorSet).flat(),
    wallMassSystem,
  });

  // iterate through the wall masses, adding doors/windows

  for (const massId in masses) {
    const wallMass = masses[massId];

    const { path } = wallMass;
    const { halfEdgeIndex: halfEdge } = path;
    // FREEFORM_WALL_HALF_EDGE_INDEX hazard: skip planarGraphAdjacentHalfEdge(-1) and
    // halfEdgeToWindowSet/halfEdgeToDoorSet/halfEdgeToWallCutSet lookups below; freeform
    // opening cuts are resolved by geometry instead.
    const isFreeform = halfEdge === FREEFORM_WALL_HALF_EDGE_INDEX;
    const oppHalfEdge = isFreeform ? halfEdge : planarGraphAdjacentHalfEdge(halfEdge);

    let wall: Wall;

    switch (wallMass.wallType) {
      case WallType.VOID: {
        wall = wallMass;
        break;
      }
      case WallType.SOLID_MASS: {
        // Freeform cuts are mass-scoped (no opposite half-edge), so apply to both sides.
        const freeformCuts = isFreeform ? freeformOpeningCutsByMassId[wallMass.id] ?? [] : undefined;
        const leftSide = wallSideNew({
          windows: isFreeform ? [] : halfEdgeToWindowSet[halfEdge],
          doors: isFreeform ? [] : halfEdgeToDoorSet[halfEdge],
          wallCuts: isFreeform ? freeformCuts : halfEdgeToWallCutSet[halfEdge],
          finishes: makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass,
            wallMaterials,
            isLeftSide: true,
          }),
          descriptor: wallMass.path.wallDescriptor.leftSide,
        });

        const rightSide = wallSideNew({
          windows: isFreeform ? [] : halfEdgeToWindowSet[oppHalfEdge],
          doors: isFreeform ? [] : halfEdgeToDoorSet[oppHalfEdge],
          wallCuts: isFreeform ? freeformCuts : halfEdgeToWallCutSet[oppHalfEdge],
          finishes: makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass,
            wallMaterials,
            isLeftSide: false,
          }),
          descriptor: wallMass.path.wallDescriptor.rightSide,
        });

        wall = solidWallWithOpenings({
          id: wallMass.id,
          levelId,
          path,
          mass: wallMass,
          leftSide,
          rightSide,
          wallMassSystem,
          mirror,
          issues,
        });
        break;
      }
      default: {
        assertUnreachable(wallMass);
      }
    }

    walls[wall.id] = wall;
  }

  return {
    ...wallMasses[levelId],
    entityType: "WALL_SYSTEM",
    id: uid(),
    pickHandle: undefined,
    docEntityHandle: undefined,
    wallIndex: new WallIndexImpl(walls, wallIndex.halfEdgeToWall, wallIndex.roomLevelIndices, wallIndex.planarGraph),
    walls,
    halfEdgeToWall,
    halfStoryWallCrossSectionFaces: [], // by default this is empty, other functions can update this value
  };
}
